import React from 'react';
import {
  GUEST_STATUSES,
  GuestGroup,
  SUMMARY_PAYMENTS_STATUSES,
  ShortGuest,
} from '@guestapp/sdk';
import {FEATURE_STATUSES} from 'utils/constants';
import {useReservation} from 'context/reservation';
import {useGuestGroup} from 'hooks';
import {useSummary} from 'context/summary';

const COMPLETE_STATUSES = [SUMMARY_PAYMENTS_STATUSES.complete];

function getPaymentFeaturesStatus(status?: SUMMARY_PAYMENTS_STATUSES) {
  if (!status) return false;
  return COMPLETE_STATUSES.includes(status);
}
function isMemberActive(member: ShortGuest) {
  return member.guestapp_status === GUEST_STATUSES.complete;
}
function isMemberPending(member: ShortGuest) {
  return member.guestapp_status === GUEST_STATUSES.verification_pending;
}
function isGuestRegistrationComplete(guestGroup?: GuestGroup) {
  const isSomeIncompletedGuest = guestGroup?.members?.some(
    member => member?.guestapp_status === GUEST_STATUSES.incomplete,
  );
  if (!guestGroup || isSomeIncompletedGuest) {
    return false;
  }
  return guestGroup?.added_number_of_guests >= guestGroup?.known_number_of_guests;
}
function getFeatureStatus(status: boolean | undefined) {
  if (status) {
    return FEATURE_STATUSES.complete;
  }
  return FEATURE_STATUSES.incomplete;
}

function useFeaturesStatuses() {
  const {data: summary} = useSummary();
  const {isBiometricMatchForAll} = useReservation();
  const {guestGroup} = useGuestGroup();
  const members = guestGroup?.members;
  const addedMembers = guestGroup?.added_number_of_guests;
  const allGuestsRegistered =
    guestGroup?.added_number_of_guests === guestGroup?.known_number_of_guests;

  const isCompletedGuestRegistration = isGuestRegistrationComplete(guestGroup);

  const isIvCompleted = React.useMemo(() => {
    if (!addedMembers) {
      return false;
    }
    if (isBiometricMatchForAll) {
      if (!allGuestsRegistered) {
        return false;
      }

      return members?.every(isMemberActive);
    }
    return !members?.some(isMemberPending);
  }, [members, isBiometricMatchForAll, addedMembers, allGuestsRegistered]);

  const deposit = getPaymentFeaturesStatus(
    summary?.property_protection?.status || undefined,
  );

  const touristTaxes = getPaymentFeaturesStatus(summary?.taxes?.status || undefined);
  const allBookingPayments = deposit && touristTaxes;
  return {
    registerGuests: getFeatureStatus(isCompletedGuestRegistration),
    verification: getFeatureStatus(isIvCompleted),
    deposit: getFeatureStatus(deposit),
    touristTaxes: getFeatureStatus(touristTaxes),
    allBookinPayments: getFeatureStatus(allBookingPayments),
  };
}

export {useFeaturesStatuses};
